/**
 * Operator campaigns list (v3-16) — route /admin/campaigns.
 *
 * The review queue only holds submitted/under_review. This lists ALL campaigns
 * (filterable by status) so in-flight / live campaigns aren't orphaned after
 * they leave the queue. Each row links to the campaign workflow detail.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminShell } from '../components/AdminShell';
import { ApiError, type CampaignRead } from '../lib/api';
import { listAllCampaigns } from '../lib/adminApi';

const STATUSES = [
  '',
  'submitted',
  'under_review',
  'approved',
  'setup',
  'live',
  'ended',
  'rejected',
  'cancelled',
];

export function AdminCampaignsListPage() {
  const [campaigns, setCampaigns] = useState<CampaignRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setCampaigns(null);
    setError(null);
    try {
      setCampaigns(await listAllCampaigns(filter || undefined));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof ApiError ? err.message : 'Could not load campaigns.');
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <AdminShell title="Campaigns">
      <div className="mb-4 flex items-center justify-between">
        <Link to="/admin" className="text-sm text-primary hover:underline">
          ← Review queue
        </Link>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink focus:border-primary focus:outline-none"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s === '' ? 'All statuses' : s}
            </option>
          ))}
        </select>
      </div>
      <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!error && !campaigns && <p className="text-sm text-ink-500">Loading…</p>}
        {campaigns && campaigns.length === 0 && (
          <p className="text-sm text-ink-500">No campaigns{filter ? ` in ${filter}` : ''}.</p>
        )}
        {campaigns && campaigns.length > 0 && (
          <ul className="divide-y divide-line">
            {campaigns.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/admin/campaigns/${c.id}`}
                  className="flex items-center justify-between py-3 hover:bg-surface-muted"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-ink">{c.name}</span>
                    <span className="block truncate text-xs text-ink-500">
                      {c.prize_title ?? 'No prize set'}
                    </span>
                  </span>
                  <span className="shrink-0 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-ink-700">
                    {c.status}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </AdminShell>
  );
}

export default AdminCampaignsListPage;
